import { useEffect } from 'react';

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  breadcrumbs?: BreadcrumbItem[];
  schemaType?: 'Organization' | 'WebSite' | 'WebPage' | 'Article' | 'FAQPage' | 'BreadcrumbList';
  schemaData?: Record<string, unknown>;
}

const BASE_URL = 'https://chigua-daily.com';

function getOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '吃瓜网每日大赛',
    url: BASE_URL,
    description: '中国领先的娱乐八卦与网红黑料爆料资讯平台，专注每日吃瓜爆料、网红塌房与黑料大瓜。实时追踪网络热门事件。',
    foundingDate: '2023',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@chigua-daily.com',
    },
    sameAs: [
      'https://weibo.com/chigua-daily',
      'https://weixin.qq.com/chigua-daily',
    ],
  };
}

function getWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '吃瓜网每日大赛',
    url: BASE_URL,
    description: '吃瓜网每日大赛官网入口与APP下载中心，专注每日吃瓜爆料、网红塌房与黑料大瓜。作为吃瓜爆料核心门面，实时追踪网络热门事件。',
    inLanguage: 'zh-CN',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE_URL}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

function getWebPageSchema(title: string, description: string, url: string, breadcrumbs?: BreadcrumbItem[]) {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description: description,
    url: url,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '吃瓜网每日大赛',
      url: BASE_URL,
    },
    about: {
      '@type': 'Thing',
      name: '娱乐八卦与网红爆料',
    },
  };

  if (breadcrumbs && breadcrumbs.length > 0) {
    schema.breadcrumb = {
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url,
      })),
    };
  }

  return schema;
}

function getArticleSchema(
  title: string,
  description: string,
  url: string,
  imageUrl: string,
  publishDate: string,
  author: string,
  breadcrumbs?: BreadcrumbItem[]
) {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: description,
    url: url,
    image: imageUrl,
    datePublished: publishDate,
    dateModified: publishDate,
    author: {
      '@type': 'Person',
      name: author,
    },
    publisher: {
      '@type': 'Organization',
      name: '吃瓜网每日大赛',
      url: BASE_URL,
    },
    inLanguage: 'zh-CN',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
  };

  if (breadcrumbs && breadcrumbs.length > 0) {
    schema.breadcrumb = {
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url,
      })),
    };
  }

  return schema;
}

function getFAQPageSchema(faqs: { question: string; answer: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

function getBreadcrumbListSchema(breadcrumbs: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export default function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType,
  breadcrumbs,
  schemaType,
  schemaData,
}: SEOProps) {
  const pageTitle = title || '吃瓜网每日大赛 | 官网入口与APP下载 | 网红大瓜黑料每日更新';
  const pageDescription = description || '吃瓜网每日大赛官网入口与APP下载中心，专注每日吃瓜爆料、网红塌房与黑料大瓜。作为吃瓜爆料核心门面，实时追踪网络热门事件，随时看最新猛料。';
  const pageKeywords = keywords || '吃瓜网每日大赛,吃瓜网每日大赛入口,吃瓜网每日大赛app,吃瓜网每日大赛下载,吃瓜网每日大赛官网,吃瓜爆料,网红黑料,每日大赛,实时追踪,最新地址,娱乐吃瓜';

  useEffect(() => {
    // Update document title
    document.title = pageTitle;

    // Update meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', pageDescription);

    // Update meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', pageKeywords);

    // Update canonical
    const canonical = canonicalUrl || BASE_URL;
    let linkCanon = document.querySelector('link[rel="canonical"]');
    if (!linkCanon) {
      linkCanon = document.createElement('link');
      linkCanon.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanon);
    }
    linkCanon.setAttribute('href', canonical);

    // Update OG
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', pageTitle);

    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (!ogDesc) {
      ogDesc = document.createElement('meta');
      ogDesc.setAttribute('property', 'og:description');
      document.head.appendChild(ogDesc);
    }
    ogDesc.setAttribute('content', pageDescription);

    let ogTypeMeta = document.querySelector('meta[property="og:type"]');
    if (!ogTypeMeta) {
      ogTypeMeta = document.createElement('meta');
      ogTypeMeta.setAttribute('property', 'og:type');
      document.head.appendChild(ogTypeMeta);
    }
    ogTypeMeta.setAttribute('content', ogType || 'website');

    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute('content', canonical);

    // Update Twitter
    let twTitle = document.querySelector('meta[name="twitter:title"]');
    if (!twTitle) {
      twTitle = document.createElement('meta');
      twTitle.setAttribute('name', 'twitter:title');
      document.head.appendChild(twTitle);
    }
    twTitle.setAttribute('content', pageTitle);

    let twDesc = document.querySelector('meta[name="twitter:description"]');
    if (!twDesc) {
      twDesc = document.createElement('meta');
      twDesc.setAttribute('name', 'twitter:description');
      document.head.appendChild(twDesc);
    }
    twDesc.setAttribute('content', pageDescription);

    // Inject JSON-LD structured data
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach((s) => s.remove());

    const schemas: Record<string, unknown>[] = [];

    // Always add Organization
    schemas.push(getOrganizationSchema());

    // Always add WebSite
    schemas.push(getWebSiteSchema());

    // Breadcrumbs
    if (breadcrumbs && breadcrumbs.length > 0) {
      schemas.push(getBreadcrumbListSchema(breadcrumbs));
    }

    // Page-specific schemas
    if (schemaType === 'Article' && schemaData) {
      schemas.push(getArticleSchema(
        pageTitle,
        pageDescription,
        canonical,
        (schemaData.imageUrl as string) || '',
        (schemaData.publishDate as string) || '',
        (schemaData.author as string) || '',
        breadcrumbs
      ));
    } else if (schemaType === 'FAQPage' && schemaData) {
      schemas.push(getFAQPageSchema((schemaData.faqs as { question: string; answer: string }[]) || []));
    } else if (schemaType === 'WebPage' || !schemaType) {
      schemas.push(getWebPageSchema(pageTitle, pageDescription, canonical, breadcrumbs));
    }

    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema, null, 2);
      document.head.appendChild(script);
    });

    // Cleanup
    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach((s) => s.remove());
    };
  }, [pageTitle, pageDescription, pageKeywords, canonicalUrl, ogType, breadcrumbs, schemaType, schemaData]);

  return null;
}